import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { useCart } from "@/context/CartContext";
import { formatPrice, isAvailable } from "@/lib/format";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import Seo from "@/components/Seo";
import BagPack from "@/components/BagPack";
import { breadcrumbLd, productLd } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPEC_ORDER = ["altitud", "clasificacion", "variedad", "proceso", "cosecha", "tueste", "lote"];

const Product = () => {
  const { id } = useParams();
  const { lang, t } = useLang();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    setError(false);
    axios
      .get(`${API}/products/${id}`)
      .then((res) => setProduct(res.data))
      .catch(() => setError(true));
  }, [id]);

  const add = () => {
    if (!isAvailable(product)) return;
    addItem(product);
    toast.success(`${product.name} ${t.encuentra.added}`);
  };

  if (error) {
    return (
      <div className="bg-[#F5F1E8] min-h-screen flex flex-col">
        <Seo
          title={t.seo.notFound.title}
          description={t.seo.notFound.description}
          path={`/cafe/${id}`}
          lang={lang}
          noindex
        />
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center gap-6 px-6 text-center">
          <p data-testid="product-not-found" className="text-[#3B2523] text-lg">
            {t.producto.notFound}
          </p>
          <Link
            to="/"
            className="bg-[#F4D35E] text-[#3B2523] font-semibold px-7 py-3 rounded-full min-h-[48px] flex items-center"
          >
            {t.producto.back}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const specs = product ? SPEC_ORDER.filter((k) => product.specs?.[k]?.[lang]) : [];
  const notes = product ? product.notes[lang] : [];

  return (
    <div className="bg-[#F5F1E8] min-h-screen overflow-x-hidden">
      {product && (
        <Seo
          title={`${product.name} ${t.seo.cafe.suffix}`}
          description={product.description[lang]}
          path={`/cafe/${product.id}`}
          type="product"
          lang={lang}
          jsonLd={[
            productLd(product, lang),
            breadcrumbLd([
              { name: "Naguarà", path: "/" },
              { name: t.nav.coffees, path: "/#coffees" },
              { name: product.name, path: `/cafe/${product.id}` },
            ]),
          ]}
        />
      )}
      <Navbar />

      <main className="pt-28 md:pt-32 pb-20 px-6 md:px-12 lg:px-20">
        <Link
          to="/"
          data-testid="product-back-link"
          className="inline-flex items-center gap-2 text-[#3B2523]/70 text-sm font-semibold hover:text-[#3B2523] transition-colors"
        >
          <ArrowLeft size={16} />
          {t.producto.back}
        </Link>

        {product ? (
          <div
            data-testid={`product-page-${product.id}`}
            className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,460px)_minmax(0,1fr)] gap-12 lg:gap-20 items-start"
          >
            {/* Pack: the same brown bag with this coffee's real sticker */}
            <div className="relative bg-[#EDE6D6] rounded-3xl border border-[#3B2523]/10 p-8 md:p-12 flex justify-center">
              {!isAvailable(product) && (
                <p
                  data-testid="product-unavailable-badge"
                  className="absolute top-5 left-5 z-10 bg-[#8C2F1E] text-[#FFF6EC] text-xs font-bold uppercase tracking-[0.12em] px-4 py-2 rounded-full shadow-lg"
                >
                  {t.avail.badge}
                </p>
              )}
              <BagPack
                product={product}
                priority
                alt={`${t.producto.packAlt} ${product.name}, 250 g`}
                testId={`product-pack-${product.id}`}
                className="w-[230px] md:w-[290px]"
                shadow="drop-shadow-[0_18px_28px_rgba(59,37,35,0.25)]"
              />
            </div>

            <div>
              <p className="text-[#3B2523]/70 uppercase tracking-[0.24em] text-xs font-semibold">
                {product.origin}
                {product.region ? ` · ${product.region[lang]}` : ""}
              </p>
              <h1
                data-testid="product-name"
                style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700 }}
                className="text-[#3B2523] tracking-tight leading-[1.08] text-[34px] sm:text-[42px] lg:text-[52px] mt-2"
              >
                {product.name}
              </h1>
              <p className="text-[#3B2523]/70 text-base md:text-lg mt-5 leading-relaxed max-w-[620px]">
                {product.description[lang]}
              </p>

              <div className="flex items-end gap-4 mt-8">
                <span data-testid="product-price" className="text-[#3B2523] text-3xl font-bold">
                  {formatPrice(product.price, lang)}
                </span>
                <span data-testid="product-weight" className="text-[#3B2523]/70 text-sm font-semibold pb-1.5">
                  {product.weight}
                </span>
              </div>

              {isAvailable(product) ? (
                <button
                  data-testid="product-add-btn"
                  onClick={add}
                  className="mt-6 w-full sm:w-auto min-h-[48px] inline-flex items-center justify-center gap-2 bg-[#3B2523] text-[#F5F1E8] font-semibold px-8 py-4 rounded-full transition-transform duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B2523] focus-visible:ring-offset-2"
                >
                  <ShoppingBag size={18} />
                  {t.encuentra.add}
                </button>
              ) : (
                <button
                  data-testid="product-add-btn"
                  disabled
                  aria-disabled="true"
                  className="mt-6 w-full sm:w-auto min-h-[48px] inline-flex items-center justify-center gap-2 bg-[#E3DCCB] text-[#6B534E] font-semibold px-8 py-4 rounded-full border border-[#3B2523]/15 cursor-not-allowed"
                >
                  {t.avail.button}
                </button>
              )}

              {!isAvailable(product) && (
                <p
                  data-testid="product-unavailable-notice"
                  className="mt-4 text-[#8C2F1E] text-sm max-w-[520px] leading-relaxed"
                >
                  {t.avail.notice}
                </p>
              )}

              {notes.length > 0 && (
                <section data-testid="product-notes" className="mt-12">
                  <h2 className="text-[#3B2523]/70 uppercase tracking-[0.2em] text-xs font-semibold">
                    {t.producto.notesTitle}
                  </h2>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {notes.map((n, i) => (
                      <span
                        key={i}
                        className="text-sm font-semibold px-4 py-2 rounded-full bg-[#EDE6D6] text-[#3B2523] border border-[#3B2523]/10"
                      >
                        {n}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {specs.length > 0 ? (
                <section data-testid="product-specs" className="mt-12">
                  <h2 className="text-[#3B2523]/70 uppercase tracking-[0.2em] text-xs font-semibold">
                    {t.producto.specsTitle}
                  </h2>
                  <dl className="mt-4 border-t border-[#3B2523]/10">
                    {specs.map((k) => (
                      <div
                        key={k}
                        className="grid grid-cols-[132px_1fr] gap-4 py-3 border-b border-[#3B2523]/10"
                      >
                        <dt className="uppercase tracking-[0.14em] text-xs font-semibold text-[#3B2523]/70 pt-0.5">
                          {t.encuentra.specs[k]}
                        </dt>
                        <dd className="text-[#3B2523] text-sm">{product.specs[k][lang]}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ) : (
                isAvailable(product) && (
                  <p data-testid="product-specs-pending" className="mt-12 text-[#3B2523]/70 text-sm italic">
                    {t.producto.pending}
                  </p>
                )
              )}
            </div>
          </div>
        ) : (
          <div data-testid="product-loading" className="mt-16 h-[420px] rounded-3xl bg-[#EDE6D6] animate-pulse" />
        )}
      </main>

      <Footer />
      <CartDrawer />
    </div>
  );
};

export default Product;
